// © 2026 AttainIP Engineering
//
// RFC-0207 W4 — mount what is on screen, account for the rest.
//
// A long list costs a render proportional to its length whatever the reader is
// looking at: 5,000 chat messages measured 25,037 mounted elements and a 799ms
// keystroke, because every keystroke re-renders a tree that size. Windowing
// removes the elements rather than the work per element, which is the only
// thing that makes the cost independent of the payload.
//
// What it must not remove:
//
//   - the scroll height. Two spacers stand in for the unmounted blocks, so the
//     bar behaves as it always did and a reader can still throw themselves at
//     the middle of a long transcript.
//   - anything PINNED. An element being edited must never be unmounted
//     mid-edit, wherever the reader has scrolled to.
//   - the ability to reach an unmounted block. `scrollToIndex` puts one on
//     screen, which is what navigation and find are built on.
//
// Heights are measured as blocks mount and remembered, so the spacers get
// truer as the reader moves. An unmeasured block uses `estimatedHeight`; being
// wrong there costs a small scroll-position correction, not a missing block.
import {
  useCallback, useEffect, useImperativeHandle, useLayoutEffect,
  useMemo, useRef, useState, forwardRef, Fragment,
} from 'react';

export interface BlockWindowHandle {
  /** Put a block on screen, mounting it if it is not. */
  scrollToIndex: (index: number, options?: { block?: 'start' | 'center' }) => void;
  /** Is this block currently mounted? */
  isMounted: (index: number) => boolean;
}

interface BlockWindowProps<T> {
  items: T[];
  /** Stable identity per item — never the array index. */
  keyOf: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  /** The element that scrolls. Windowing is relative to it. */
  scrollRootRef: React.RefObject<HTMLElement | null>;
  /** Height to assume for a block that has not been measured yet. */
  estimatedHeight?: number;
  /** Extra blocks kept mounted either side of the viewport. */
  overscan?: number;
  /** Indices that must stay mounted whatever the scroll position — the block
   *  holding an open editor, chiefly. */
  pinnedIndices?: readonly number[];
  /** Below this many items, windowing is not worth its own bookkeeping. */
  disableBelow?: number;
  /** Space between logical rows, included in their virtual extent. */
  gap?: number;
}

function BlockWindowInner<T>(
  {
    items, keyOf, renderItem, scrollRootRef,
    estimatedHeight = 120, overscan = 6, pinnedIndices, disableBelow = 60, gap = 0,
  }: BlockWindowProps<T>,
  ref: React.ForwardedRef<BlockWindowHandle>,
) {
  const heightsRef = useRef<Map<string, number>>(new Map());
  const [range, setRange] = useState({ start: 0, end: items.length });
  /** Bumped when a measurement changes, so the offsets recompute. The VALUE is
   *  the signal — depending on the setter would be depending on a constant,
   *  and the offsets would never move off their estimates. */
  const [measureTick, bumpMeasureTick] = useState(0);
  const windowed = items.length >= disableBelow;

  /** Offset of each block from the top, using measured heights where known. */
  const offsets = useMemo(() => {
    // An unseen row keeps its estimate. Learning one tall row must not
    // change the virtual height of hundreds of unrelated unseen rows.
    const measuredHeights = heightsRef.current;
    const out: number[] = new Array(items.length + 1);
    out[0] = 0;
    for (let i = 0; i < items.length; i += 1) {
      const measured = measuredHeights.get(keyOf(items[i], i));
      out[i + 1] = out[i] + (measured ?? (estimatedHeight + (i > 0 ? gap : 0)));
    }
    return out;
    // Heights live in a ref, deliberately: holding them in state would copy a
    // map of every measured block on every measurement. `measureTick` is the
    // invalidation token that says one changed — it is not read in the body,
    // which is exactly what the rule below objects to and why it is silenced
    // here rather than obeyed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, keyOf, estimatedHeight, measureTick, gap]);

  /** The top of the list, in the scroll root's own coordinates.
   *
   *  ⚠️ A windowed list does NOT necessarily start at the top of the thing that
   *  scrolls. A document pane has a page heading above it; a chat has its
   *  version band; a pane can hold several windowed lists one after another.
   *  Treating `scrollTop` as an offset into the list is only correct when the
   *  list happens to start at zero, and silently mis-ranges everything that
   *  does not — mounting the wrong blocks, and scrolling to the wrong place.
   *
   *  Measured from the leading spacer, which is always the list's first node. */
  const listOriginRef = useRef<HTMLDivElement | null>(null);
  const originOf = useCallback((root: HTMLElement) => {
    const marker = listOriginRef.current;
    if (!marker) return 0;
    return marker.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
  }, []);

  const recomputeRange = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || !windowed) {
      setRange({ start: 0, end: items.length });
      return;
    }
    const origin = originOf(root);
    const top = root.scrollTop - origin;
    const bottom = top + root.clientHeight;

    let start = 0;
    while (start < items.length && offsets[start + 1] < top) start += 1;
    let end = start;
    while (end < items.length && offsets[end] < bottom) end += 1;

    setRange((current) => {
      const next = {
        start: Math.max(0, start - overscan),
        end: Math.min(items.length, end + overscan),
      };
      return current.start === next.start && current.end === next.end ? current : next;
    });
  }, [scrollRootRef, windowed, items.length, offsets, overscan, originOf]);

  // The scroll root belongs to an ANCESTOR, and React attaches a host ref
  // after the layout effects of the children beneath it. So on the first pass
  // `scrollRootRef.current` is still null, the range falls back to "all of
  // them", and nothing runs again — the list mounts whole and stays that way.
  //
  // `rootTick` is bumped once the tree has committed, which is when the ref is
  // really there, and both effects below depend on it.
  const [rootTick, setRootTick] = useState(0);
  useEffect(() => {
    if (scrollRootRef.current) setRootTick((n) => (n === 0 ? 1 : n));
  }, [scrollRootRef]);

  useLayoutEffect(() => { recomputeRange(); }, [recomputeRange, rootTick]);

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || !windowed) return;
    const onScroll = () => recomputeRange();
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [scrollRootRef, windowed, recomputeRange, rootTick]);

  /** Measure a mounted block and remember it.
   *
   *  ⚠️ WITH ITS MARGINS. A spacer stands in for unmounted blocks by height
   *  alone, but a mounted block also occupies the margin its container gives
   *  it (a chat transcript spaces its blocks with a 20 px top margin). Measured
   *  without the margin, every block that left the window shortened the page
   *  by 20 px; the browser's scroll anchoring moved the scroll position by the
   *  same 20 px to keep what was on screen still; the range recomputed on that
   *  scroll and the block qualified again; mounted, the page grew by 20 px,
   *  and so on — scrollTop and scrollHeight alternating ±20 px on every frame
   *  for as long as the reader rested within a margin of a block boundary
   *  (Tom's traces, 2026-09-11; reproduced at 15 of 60 resting positions in
   *  `windowRest.spec.ts`). Counting the margin makes a block's share of the
   *  page the same whether it is mounted or stood in for. */
  const measure = useCallback((key: string, el: HTMLElement | null) => {
    if (!el) return;
    const inner = el.offsetHeight;
    if (inner <= 0) return;
    const style = getComputedStyle(el);
    const margins = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
    const height = inner + margins;
    const known = heightsRef.current.get(key);
    if (known !== undefined && Math.abs(known - height) < 2) return;
    heightsRef.current.set(key, height);
    bumpMeasureTick((n) => n + 1);
  }, []);

  /** The mounted slots, by index — how a scroll-to-index checks where it
   *  actually landed instead of where it hoped to. */
  const slotElsRef = useRef<Map<number, HTMLElement>>(new Map());
  const registerSlot = useCallback((index: number, el: HTMLElement | null) => {
    if (el) slotElsRef.current.set(index, el);
    else slotElsRef.current.delete(index);
  }, []);

  /** Latest offsets, for the settle loop to read without being re-created. */
  const offsetsRef = useRef(offsets);
  offsetsRef.current = offsets;

  const settleRafRef = useRef<number | null>(null);
  const cancelSettle = useCallback(() => {
    if (settleRafRef.current !== null) cancelAnimationFrame(settleRafRef.current);
    settleRafRef.current = null;
  }, []);
  useEffect(() => cancelSettle, [cancelSettle]);

  // ⚠️ SCROLL-TO-INDEX CANNOT AIM WITH OFFSETS ALONE, AND TWO EARLIER ATTEMPTS
  // HERE GOT THAT WRONG.
  //
  // The offsets are estimates for every block nobody has measured, and a
  // scroll-to-index is precisely the move that mounts a batch of new blocks —
  // which are then measured, which changes the offsets, which slides the target
  // out from under the position we just set. Measured in Chromium at 2,000
  // messages: aiming at block 1500 landed on 1516, and after correcting
  // against the offsets it was still 247px out and off screen.
  //
  // The first attempt corrected once and stopped, before the measurements that
  // move things had landed. The second waited for the offsets to "settle",
  // which they appear to do between measurement bursts. Both were guessing at
  // the target's position.
  //
  // Once the target block is MOUNTED there is no need to guess: its real
  // rectangle says exactly where it is. So the loop below closes the gap
  // against `getBoundingClientRect`, which cannot be wrong, and only falls back
  // to the offsets while the block is still unmounted and has no rectangle.
  //
  // jsdom cannot see any of this — `offsetHeight` is 0 there, so nothing is
  // measured and nothing ever moves. It took a real browser to find it, and it
  // affects every caller of `scrollToIndex`, not just find.
  const settleOn = useCallback((index: number, block: 'start' | 'center') => {
    cancelSettle();
    const root = scrollRootRef.current;
    if (!root) return;

    let frames = 0;
    let stable = 0;

    // The reader's own scrolling always wins. If they move while we are still
    // converging, we stop — being dragged around by a finished navigation is
    // worse than landing a few pixels off.
    const abort = () => { cancelSettle(); detach(); };
    const detach = () => {
      root.removeEventListener('wheel', abort);
      root.removeEventListener('touchstart', abort);
    };
    root.addEventListener('wheel', abort, { passive: true, once: true });
    root.addEventListener('touchstart', abort, { passive: true, once: true });

    const step = () => {
      settleRafRef.current = null;
      const el = slotElsRef.current.get(index);
      if (el) {
        const rootRect = root.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const desiredTop = block === 'center'
          ? rootRect.top + Math.max(0, (root.clientHeight - elRect.height) / 2)
          : rootRect.top;
        const delta = elRect.top - desiredTop;
        if (Math.abs(delta) <= 1) {
          // Two quiet frames, not one: a single frame can be quiet in the gap
          // between two measurement bursts.
          stable += 1;
          if (stable >= 2) { detach(); return; }
        } else {
          stable = 0;
          root.scrollTop += delta;
        }
      } else {
        // No rectangle to correct against yet — aim with what we have, which
        // at least moves the mounted range toward the target.
        root.scrollTop = originOf(root) + (offsetsRef.current[index] ?? 0);
      }
      frames += 1;
      if (frames >= 60) { detach(); return; }   // ~1s. A loop must be bounded.
      settleRafRef.current = requestAnimationFrame(step);
    };
    settleRafRef.current = requestAnimationFrame(step);
  }, [cancelSettle, scrollRootRef, originOf]);

  useImperativeHandle(ref, () => ({
    scrollToIndex: (index, options) => {
      const root = scrollRootRef.current;
      if (!root) return;
      const clamped = Math.max(0, Math.min(items.length - 1, index));
      // Mount it first — a block that is not there cannot be scrolled to, and
      // this is what navigation and find rely on.
      setRange((current) => ({
        start: Math.min(current.start, Math.max(0, clamped - overscan)),
        end: Math.max(current.end, Math.min(items.length, clamped + overscan + 1)),
      }));
      const block = options?.block ?? 'start';
      // Assigned rather than `scrollTo`: this is the primitive every
      // environment implements, and the recompute that follows reads it back.
      // This is only the opening move — see `settleOn`.
      root.scrollTop = originOf(root) + (offsets[clamped] ?? 0);
      settleOn(clamped, block);
    },
    isMounted: (index) => !windowed || slotElsRef.current.has(index),
  }), [scrollRootRef, items.length, offsets, overscan, range, windowed, settleOn, originOf]);

  // One ordered sibling list. Pinned rows replace their share of an omitted
  // interval; they never sit on top of a spacer that already counts them.
  // Keys stay in this same list across range changes, preserving DOM identity.
  const first = Math.min(range.start, items.length);
  const last = Math.min(range.end, items.length);
  const indices = windowed
    ? [...new Set([
      ...Array.from({ length: Math.max(0, last - first) }, (_, n) => first + n),
      ...(pinnedIndices ?? []).filter((i) => Number.isInteger(i) && i >= 0 && i < items.length),
    ])].sort((a, b) => a - b)
    : items.map((_, i) => i);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  const addGap = (end: number) => {
    if (end <= cursor) return;
    nodes.push(<div key={`gap-${cursor}-${end}`} aria-hidden
      data-block-window-spacer={cursor === 0 ? 'above' : end === items.length ? 'below' : 'gap'}
      style={{ height: offsets[end] - offsets[cursor], flexShrink: 0, overflowAnchor: 'none' }} />);
  };
  for (const i of indices) {
    addGap(i);
    nodes.push(<BlockSlot key={keyOf(items[i], i)} rowKey={keyOf(items[i], i)} gap={i > 0 ? gap : 0}
      onMeasure={(el) => measure(keyOf(items[i], i), el)} onSlot={(el) => registerSlot(i, el)}>
      {renderItem(items[i], i)}
    </BlockSlot>);
    cursor = i + 1;
  }
  addGap(items.length);
  return <Fragment>
    {windowed && <div ref={listOriginRef} aria-hidden style={{ height: 0, flexShrink: 0, overflowAnchor: 'none' }} />}
    {nodes}
  </Fragment>;

}

/** A mounted block, measured on mount and whenever its content resizes. */
function BlockSlot(
  { children, onMeasure, onSlot, rowKey, gap = 0 }: {
    rowKey?: string;
    gap?: number;
    children: React.ReactNode;
    onMeasure: (el: HTMLElement | null) => void;
    onSlot?: (el: HTMLElement | null) => void;
  },
) {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    onSlot?.(ref.current);
    return () => onSlot?.(null);
    // Registration only — re-running it on every render would churn the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSlot]);
  useLayoutEffect(() => {
    onMeasure(ref.current);
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => onMeasure(ref.current));
    observer.observe(el);
    return () => observer.disconnect();
  });
  return <div ref={ref} data-block-window-row={rowKey} style={{ paddingTop: gap, display: 'flow-root', flexShrink: 0 }}>{children}</div>;
}

export const BlockWindow = forwardRef(BlockWindowInner) as <T>(
  props: BlockWindowProps<T> & { ref?: React.ForwardedRef<BlockWindowHandle> },
) => React.ReactElement;
